import { makeAutoObservable } from 'mobx'
import dayjs from 'dayjs'
import type { IDropdownItem } from 'shared/ui'
import { DayjsFormats } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'

export type ISendingHoursActionKey = 'continue' | 'skip'

export type IInitSendingHours = {
  isSendingHours: boolean
  startDate?: string
  stopDate?: string
  action?: ISendingHoursActionKey
}

export class SendingHoursStore {
  private _toggleCallBack: ((status: boolean) => void) | null = null

  constructor(toggleCallBack?: (status: boolean) => void) {
    makeAutoObservable(this)

    this._toggleCallBack = toggleCallBack || null
  }

  private _dropdownId = ''
  private _isSendingHours = false
  private _isSendingHoursDisabled = false
  private _selectedAction: ISendingHoursActionKey = 'continue'
  private _startDate: Date | null = null
  private _stopDate: Date | null = null
  private _show = false

  init = ({ isSendingHours, startDate, stopDate, action }: IInitSendingHours) => {
    if (!isSendingHours) return

    this._isSendingHours = isSendingHours
    this._startDate = uiStore
      .dayjs(`${uiStore.dayjs().format(DayjsFormats.date2)} ${startDate}`)
      .toDate()
    this._stopDate = uiStore
      .dayjs(`${uiStore.dayjs().format(DayjsFormats.date2)} ${stopDate}`)
      .toDate()
    this._selectedAction = action ?? 'continue'
  }

  setShow = (show: boolean) => {
    this._show = show
  }

  setDropdownId = (dropdownId: string) => {
    this._dropdownId = dropdownId
  }

  toggleSendingHours = () => {
    if (this.isSendingHoursDisabled) return

    this._isSendingHours = !this._isSendingHours
    this._toggleCallBack?.(this._isSendingHours)
  }

  setSendingHoursDisabled = (value: boolean) => {
    this._isSendingHoursDisabled = value
  }

  setAction = (key: ISendingHoursActionKey) => {
    this._selectedAction = key
  }

  setStartDate = (date: Date | null) => {
    this._startDate = date
  }

  setStopDate = (date: Date | null) => {
    this._stopDate = date
  }

  get startDate() {
    return this._startDate
  }
  get stopDate() {
    return this._stopDate
  }

  get filteredRemainingTime() {
    return this.stopDate ? uiStore.dayjs(this.stopDate).add(-1, 'second').toDate() : undefined
  }

  get filteredPassedTime() {
    return this.startDate ? uiStore.dayjs(this.startDate).add(1, 'second').toDate() : undefined
  }

  get actionItems() {
    const items: IDropdownItem[] = [
      { id: 'continue', label: 'Continue next day during same window' },
      { id: 'skip', label: 'Stop and skip remaining messages' },
    ]
    return items
  }

  get selectedAction() {
    return this._selectedAction
  }

  get isSendingHours() {
    return this._isSendingHours
  }

  get isSendingHoursDisabled() {
    return this._isSendingHoursDisabled
  }

  get dropdownId() {
    return this._dropdownId
  }

  get isEnabled() {
    return !this.isSendingHoursDisabled && this.isSendingHours
  }

  get restrictSendingHoursText() {
    if (this.isEnabled) {
      const start = this.startDate ? dayjs(this.startDate).format(DayjsFormats.time) : ''
      const stop = this.startDate ? dayjs(this.stopDate).format(DayjsFormats.time) : ''

      return `Between ${start} - ${stop} `
    }
    return ''
  }

  get params() {
    if (this.isEnabled) {
      return {
        start: this.startDate ? dayjs(this.startDate).format(DayjsFormats.time3) : null,
        stop: this.stopDate ? dayjs(this.stopDate).format(DayjsFormats.time3) : null,
        action: this.selectedAction,
      }
    }

    return null
  }

  get show() {
    return this._show
  }
}
